import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, ClipboardList, Lock, Clock, ShieldCheck, Pencil, Mic, Building2, Route, GraduationCap, HeartPulse, Leaf, TrendingUp, Shield, Droplets, Lightbulb, Store, Trophy, Wheat, Users, Briefcase, Megaphone, TreePine, HeartHandshake } from 'lucide-react';
import { getIntro, startSurvey, createInvitation, API_BASE_URL } from '../api';
import toast from 'react-hot-toast';

const DEFAULT_TEXT = "Votre avis compte. Votre parole construit. Cette enquête est anonyme, volontaire et confidentielle. Elle vise à mieux comprendre vos attentes et à construire ensemble une vision partagée pour l'avenir de Khombole.";

const HomePage = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [introData, setIntroData] = useState({ text: '', audio_url: null });
  const [starting, setStarting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [phone, setPhone] = useState('');
  const [inviteStep, setInviteStep] = useState('form'); // form | sending | link
  const [inviteUrl, setInviteUrl] = useState('');

  useEffect(() => {
    loadIntro();
  }, []);

  const loadIntro = async () => {
    try {
      const res = await getIntro();
      setIntroData(res.data);
      setLoaded(true);
      if (res.data.audio_url) {
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().catch(() => {});
          }
        }, 800);
      }
    } catch (err) {
      setLoaded(true);
    }
  };

  const handleSendInvite = async () => {
    if (!phone.trim()) {
      toast.error('Veuillez entrer votre numéro WhatsApp');
      return;
    }
    setInviteStep('sending');
    try {
      const res = await createInvitation({ phone: phone.trim(), invited_by: 'self' });
      const token = res.data.token;
      const url = res.data.invite_url;
      setInviteUrl(url);
      setInviteStep('link');
      // Open WhatsApp with pre-filled message
      const message = `Bonjour,\n\nLa Commune de Khombole vous invite à participer à l'enquête citoyenne sur le bilan du mandat 2022-2027.\n\nCliquez sur votre lien personnel : ${url}\n\nCe lien est personnel et utilisable une seule fois.\n\nMerci pour votre participation.`;
      const waUrl = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(waUrl, '_blank');
    } catch (err) {
      setInviteStep('form');
      toast.error(err.response?.data?.message || 'Erreur lors de la création du lien');
    }
  };

  if (!loaded) {
    return (
      <div className="kh-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="kh-spinner" />
      </div>
    );
  }

  return (
    <div className="kh-root">
      {/* Hidden auto-play audio */}
      {introData.audio_url && (
        <audio ref={audioRef} src={introData.audio_url ? `${API_BASE_URL}${introData.audio_url}` : ''} style={{ display: 'none' }} />
      )}

      <div className="kh-topbar">
        <div className="kh-topbar-left">
          <div className="kh-logo-wrap">
            {logoError ? (
              <div className="kh-logo-fallback">K</div>
            ) : (
              <img
                src="/logo.webp"
                alt="Logo Commune de Khombole"
                className="kh-logo-img"
                onError={() => setLogoError(true)}
              />
            )}
          </div>
          <div>
            <div className="kh-topbar-name">Commune de Khombole</div>
            <div className="kh-topbar-sub">République du Sénégal</div>
          </div>
        </div>
        <button className="kh-admin-btn" onClick={() => navigate('/admin')}>
          <Settings size={15} /> Administration
        </button>
      </div>

      <div className="kh-hero">
        <div className="kh-hero-bg" />
        <div className="kh-hero-accent" />

        <div className="kh-badge">
          <ClipboardList size={16} />
          Bilan du mandat municipal 2022–2027
        </div>

        <div className="kh-logo-hero">
          {logoError ? (
            <div className="kh-logo-hero-fallback">K</div>
          ) : (
            <img
              src="/logo.webp"
              alt="Logo"
              className="kh-logo-hero-img"
              onError={() => setLogoError(true)}
            />
          )}
        </div>

        <h1 className="kh-title">Enquête Citoyenne</h1>
        <div className="kh-subtitle">Khombole construit son avenir avec vous</div>

        <div className="kh-divider-line">
          <div className="kh-divider-dot kh-dot-green" />
          <div className="kh-divider-dot kh-dot-yellow" />
          <div className="kh-divider-dot kh-dot-red" />
        </div>

        <p className="kh-desc">
          {introData.text || DEFAULT_TEXT}
        </p>

        <div className="kh-meta-pills">
          <div className="kh-pill">
            <Lock size={16} className="kh-pill-icon" />
            Anonyme &amp; confidentielle
          </div>
          <div className="kh-pill">
            <Clock size={16} className="kh-pill-icon" />
            10 à 15 minutes
          </div>
          <div className="kh-pill">
            <ShieldCheck size={16} className="kh-pill-icon" />
            Volontaire
          </div>
        </div>

        <div className="kh-cta-row" style={{ flexDirection: 'column', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
          {inviteStep === 'form' && (
            <>
              <style>{`
                .whatsapp-input::placeholder {
                  color: #666 !important;
                  font-weight: 700 !important;
                  opacity: 1 !important;
                }
                .whatsapp-input::-webkit-input-placeholder {
                  color: #666 !important;
                  font-weight: 700 !important;
                }
                .whatsapp-input::-moz-placeholder {
                  color: #666 !important;
                  font-weight: 700 !important;
                }
              `}</style>
              <div style={{ textAlign: 'left', width: '100%' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#000000', marginBottom: '8px', fontWeight: '700' }}>Numéro WhatsApp</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex: 221771234567"
                  className="whatsapp-input"
                  style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '2px solid #999', background: '#ffffff', color: '#000000', fontSize: '16px', fontWeight: '700', outline: 'none' }}
                />
              </div>
              <button className="kh-btn-primary" onClick={handleSendInvite} style={{ width: '100%', marginTop: '8px' }}>
                <ClipboardList size={20} /> Recevoir mon lien d'invitation
              </button>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Un lien personnel à usage unique vous sera envoyé</p>
            </>
          )}
          {inviteStep === 'sending' && (
            <div style={{ color: 'white', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="kh-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }} /> Création de votre lien...
            </div>
          )}
          {inviteStep === 'link' && (
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '24px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)' }}>
              <p style={{ color: '#FFD54F', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Votre lien est prêt !</p>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '16px', lineHeight: '1.5' }}>Si WhatsApp ne s'est pas ouvert automatiquement, copiez ce lien :</p>
              <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'white', wordBreak: 'break-all', marginBottom: '16px', fontFamily: 'monospace' }}>{inviteUrl}</div>
              <button onClick={() => { navigator.clipboard.writeText(inviteUrl); toast?.success?.('Lien copié !'); }} className="kh-btn-primary" style={{ marginBottom: '8px' }}>
                Copier le lien
              </button>
              <button onClick={() => navigate(`/invitation/${inviteUrl.split('/').pop()}`)} className="kh-btn-audio" style={{ width: '100%' }}>
                Commencer l'enquête maintenant
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="kh-section">
        <div className="kh-section-label">Thèmes abordés</div>
        <div className="kh-themes-grid">
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Building2 size={24} /></div>
            <div className="kh-theme-name">Services publics</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Route size={24} /></div>
            <div className="kh-theme-name">Voirie et infrastructures</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Droplets size={24} /></div>
            <div className="kh-theme-name">Eau et assainissement</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Lightbulb size={24} /></div>
            <div className="kh-theme-name">Éclairage public</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><GraduationCap size={24} /></div>
            <div className="kh-theme-name">Éducation</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><HeartPulse size={24} /></div>
            <div className="kh-theme-name">Santé</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Trophy size={24} /></div>
            <div className="kh-theme-name">Sport, culture et jeunesse</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Shield size={24} /></div>
            <div className="kh-theme-name">Sécurité publique</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Store size={24} /></div>
            <div className="kh-theme-name">Marchés et économie locale</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Leaf size={24} /></div>
            <div className="kh-theme-name">Environnement et ordures</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Wheat size={24} /></div>
            <div className="kh-theme-name">Agriculture et souveraineté alimentaire</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Users size={24} /></div>
            <div className="kh-theme-name">Participation citoyenne</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Briefcase size={24} /></div>
            <div className="kh-theme-name">Formation professionnelle et emploi</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><Megaphone size={24} /></div>
            <div className="kh-theme-name">Communication et transparence</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><TreePine size={24} /></div>
            <div className="kh-theme-name">Foresterie et reboisement</div>
          </div>
          <div className="kh-theme-card">
            <div className="kh-theme-icon"><HeartHandshake size={24} /></div>
            <div className="kh-theme-name">Autonomisation des femmes</div>
          </div>
        </div>
      </div>

      <div className="kh-mayor-section">
        <div className="kh-mayor-avatar">MB</div>
        <div className="kh-mayor-text">
          <div className="kh-mayor-quote">
            « Ensemble, évaluons le chemin parcouru et traçons la voie vers un Khombole encore plus prospère et solidaire. »
          </div>
          <div className="kh-mayor-name">Magueye BOYE</div>
          <div className="kh-mayor-role">Maire de la Commune de Khombole</div>
        </div>
      </div>

      <div className="kh-footer">
        <div className="kh-footer-text">© 2026 Commune de Khombole — Tous droits réservés</div>
        <div className="kh-footer-seal">
          <Shield size={16} /> Données protégées et anonymisées
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');

        :root {
          --color-background-primary: #fffdf8;
          --color-background-secondary: #fff9ef;
          --color-text-primary: #1a1a1a;
          --color-text-secondary: #5a5a5a;
          --color-border-tertiary: #ede5d8;
          --logo-green: #1b5e20;
          --logo-yellow: #f9a825;
          --logo-red: #c62828;
        }

        .kh-root {
          font-family: 'Inter', sans-serif;
          background: var(--color-background-primary);
          min-height: 100vh;
          color: var(--color-text-primary);
          overflow-x: hidden;
        }

        .kh-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 40px;
          border-bottom: 0.5px solid var(--color-border-tertiary);
          background: var(--color-background-primary);
        }

        .kh-topbar-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .kh-logo-wrap {
          width: 54px;
          height: 54px;
          border-radius: 50%;
          background: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
          border: 2px solid var(--logo-yellow);
        }

        .kh-logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 4px;
        }

        .kh-logo-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--logo-yellow);
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 700;
          background: var(--logo-green);
        }

        .kh-topbar-name {
          font-size: 15px;
          font-weight: 700;
          color: var(--color-text-primary);
          letter-spacing: 0.4px;
        }

        .kh-topbar-sub {
          font-size: 12px;
          color: var(--color-text-secondary);
          margin-top: 2px;
          font-weight: 500;
        }

        .kh-admin-btn {
          font-size: 13px;
          color: var(--color-text-secondary);
          border: 0.5px solid var(--color-border-tertiary);
          border-radius: 8px;
          padding: 8px 16px;
          cursor: pointer;
          background: transparent;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.15s;
          font-family: 'Inter', sans-serif;
          font-weight: 500;
        }

        .kh-admin-btn:hover { background: var(--color-background-secondary); border-color: var(--logo-yellow); color: var(--logo-green); }
        .kh-admin-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .kh-hero {
          position: relative;
          padding: 60px 32px 64px;
          text-align: center;
          border-bottom: 0.5px solid var(--color-border-tertiary);
          overflow: hidden;
        }

        .kh-hero-bg {
          position: absolute;
          inset: 0;
          background: var(--color-background-secondary);
          opacity: 0.5;
        }

        .kh-hero-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          background: linear-gradient(90deg, var(--logo-green) 33%, var(--logo-yellow) 33%, var(--logo-yellow) 66%, var(--logo-red) 66%);
        }

        .kh-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1.1px;
          text-transform: uppercase;
          color: var(--logo-green);
          border: 1px solid var(--logo-yellow);
          border-radius: 30px;
          padding: 7px 18px;
          margin-bottom: 22px;
          position: relative;
          background: white;
          box-shadow: 0 2px 10px rgba(249,168,37,0.12);
        }

        .kh-logo-hero {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: white;
          box-shadow: 0 12px 40px rgba(0,0,0,0.12), 0 0 0 5px rgba(249,168,37,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 22px;
          overflow: hidden;
          position: relative;
          z-index: 1;
        }

        .kh-logo-hero-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }

        .kh-logo-hero-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--logo-yellow);
          font-family: 'Playfair Display', serif;
          font-size: 52px;
          font-weight: 700;
          background: var(--logo-green);
        }

        .kh-title {
          font-family: 'Playfair Display', serif;
          font-size: 48px;
          font-weight: 700;
          color: var(--color-text-primary);
          line-height: 1.15;
          margin: 0 0 12px;
          position: relative;
          letter-spacing: -1px;
        }

        .kh-subtitle {
          font-size: 19px;
          color: var(--color-text-secondary);
          margin: 0 0 24px;
          font-weight: 500;
          position: relative;
        }

        .kh-divider-line {
          width: 110px;
          height: 4px;
          background: transparent;
          margin: 0 auto 26px;
          border-radius: 2px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .kh-divider-dot {
          width: 28px;
          height: 4px;
          border-radius: 2px;
        }

        .kh-dot-green { background: var(--logo-green); }
        .kh-dot-yellow { background: var(--logo-yellow); }
        .kh-dot-red { background: var(--logo-red); }

        .kh-desc {
          font-size: 17px;
          color: var(--color-text-secondary);
          max-width: 620px;
          margin: 0 auto 40px;
          line-height: 1.8;
          position: relative;
          font-weight: 400;
        }

        .kh-meta-pills {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 40px;
          position: relative;
        }

        .kh-pill {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 14px;
          color: var(--color-text-secondary);
          background: white;
          border: 0.5px solid var(--color-border-tertiary);
          border-radius: 30px;
          padding: 7px 16px;
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }

        .kh-pill-icon { color: var(--logo-green); }

        .kh-cta-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
          position: relative;
        }

        .kh-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, var(--logo-green), #2e7d32);
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 16px 34px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          box-shadow: 0 8px 24px rgba(27,94,32,0.25);
        }

        .kh-btn-primary:hover { background: linear-gradient(135deg, #154f18, #1b5e20); transform: translateY(-2px); box-shadow: 0 12px 30px rgba(27,94,32,0.32); }
        .kh-btn-primary:active { transform: scale(0.98); }
        .kh-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

        .kh-btn-audio {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: white;
          color: var(--logo-red);
          border: 1.5px solid var(--logo-red);
          border-radius: 12px;
          padding: 16px 34px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
          box-shadow: 0 4px 16px rgba(198,40,40,0.08);
        }

        .kh-btn-audio:hover { background: var(--logo-red); color: white; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(198,40,40,0.2); }
        .kh-btn-audio:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

        .kh-section {
          padding: 56px 40px;
          border-bottom: 0.5px solid var(--color-border-tertiary);
          max-width: 1100px;
          margin: 0 auto;
        }

        .kh-section-label {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: var(--logo-green);
          margin-bottom: 24px;
          text-align: center;
        }

        .kh-themes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 16px;
        }

        .kh-theme-card {
          background: white;
          border: 0.5px solid var(--color-border-tertiary);
          border-radius: 14px;
          padding: 22px 18px;
          text-align: center;
          transition: all 0.2s;
          cursor: default;
          box-shadow: 0 4px 14px rgba(0,0,0,0.04);
        }

        .kh-theme-card:hover {
          border-color: var(--logo-yellow);
          transform: translateY(-4px);
          box-shadow: 0 10px 28px rgba(249,168,37,0.12);
        }

        .kh-theme-icon {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          background: linear-gradient(135deg, #fff9ef, #fff3e0);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
          color: var(--logo-green);
          border: 1px solid var(--color-border-tertiary);
        }

        .kh-theme-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text-primary);
          line-height: 1.3;
        }

        .kh-mayor-section {
          padding: 48px 40px;
          border-bottom: 0.5px solid var(--color-border-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
          flex-wrap: wrap;
          max-width: 900px;
          margin: 0 auto;
        }

        .kh-mayor-avatar {
          width: 84px;
          height: 84px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--logo-green), #2e7d32);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 700;
          flex-shrink: 0;
          border: 4px solid rgba(249,168,37,0.35);
          box-shadow: 0 6px 20px rgba(27,94,32,0.2);
        }

        .kh-mayor-text { flex: 1; min-width: 250px; text-align: center; }

        .kh-mayor-quote {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          color: var(--color-text-primary);
          line-height: 1.55;
          margin-bottom: 14px;
          font-style: italic;
          font-weight: 500;
        }

        .kh-mayor-name {
          font-size: 15px;
          font-weight: 700;
          color: var(--logo-green);
        }

        .kh-mayor-role {
          font-size: 13px;
          color: var(--color-text-secondary);
          margin-top: 3px;
          font-weight: 500;
        }

        .kh-footer {
          padding: 28px 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .kh-footer-text {
          font-size: 13px;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        .kh-footer-seal {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        .kh-spinner {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 4px solid var(--color-border-tertiary);
          border-top-color: var(--logo-green);
          animation: spin 1s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 700px) {
          .kh-topbar { padding: 14px 20px; }
          .kh-logo-wrap { width: 46px; height: 46px; }
          .kh-topbar-name { font-size: 13px; }
          .kh-hero { padding: 48px 20px 52px; }
          .kh-logo-hero { width: 96px; height: 96px; }
          .kh-title { font-size: 38px; }
          .kh-subtitle { font-size: 16px; }
          .kh-desc { font-size: 15px; margin-bottom: 30px; }
          .kh-section { padding: 40px 20px; }
          .kh-mayor-section { padding: 36px 20px; }
          .kh-footer { padding: 22px 20px; }
          .kh-themes-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .kh-theme-card { padding: 18px 12px; }
          .kh-btn-primary, .kh-btn-audio { padding: 14px 26px; font-size: 15px; }
          .kh-mayor-quote { font-size: 17px; }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
